import { useContext, useMemo } from "react"
import { useRef } from "react"
import { createContext } from "react"
import PersonAddIcon from "@material-ui/icons/PersonAdd"
import { useSelector } from "react-redux"
import { Children } from "react"
import { cloneElement } from "react"
import React, { Fragment, useCallback, useState, useEffect } from "react"
import {
  Box,
  TextField,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  MenuItem,
  Menu,
  InputAdornment,
  Tooltip,
} from "@material-ui/core"
import { useTheme } from "@material-ui/styles"
import CancelIcon from "@material-ui/icons/Cancel"
import AddIcon from "@material-ui/icons/Add"
import FileCopyIcon from "@material-ui/icons/FileCopy"
import EmojiFlagsIcon from "@material-ui/icons/EmojiFlags"
import ExpandMoreIconOutlined from "@material-ui/icons/ExpandMoreOutlined"
import useStyles from "./styles"
import AutocompleteBilling from "../AutocompleteBilling/index"
import StatusChecker from "../../Admin/CrewStatusTable/CrewStatusTableRow/StatusChecker/index"
import { Formik, useFormikContext } from "formik"
import classnames from "clsx"
import isUuid from "../../../../utils/isUuid"
import copyToClipboard from "copy-to-clipboard"
import BillingContext from "../../../../pages/Shoot/pages/Billing/BillingContext"
import formatNumberWithCommas from "../../../../utils/formatNumberWithCommas"
import CreateIcon from "@material-ui/icons/Create"
import useIsGTT from "../../../../hooks/useIsGTT"
import useActiveCompanyAdesk from "../../../../hooks/useActiveCompanyAdesk"
import useHaveGTT from "../../../../hooks/useHaveGTT"
import capitalizeFirstLetter from "../../../../utils/capitalizeFirstLetter"
import CloseIcon from "@material-ui/icons/Close"
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd"

const ExpenseTableContext = createContext()

const useGridTemplateColumnStyle = columns => {
  const gridData = columns
    .map(
      column =>
        `[${column.dataProperty}-start] ${
          column.gridSize ? column.gridSize : "1fr"
        }`
    )
    .join(" ")
  return {
    gridTemplateColumns: `${gridData} [${
      columns[columns.length - 1].dataProperty
    }-end]`,
  }
}

const useExpenseTable = () => {
  return useContext(ExpenseTableContext)
}
const viewportWidth = window.innerWidth || document.documentElement.clientWidth

export const HeaderItem = props => {
  const classes = useStyles()

  return <Box {...props} className={classes.headerItem} />
}

export const Header = ({ columns, style, ...props }) => {
  const classes = useStyles()

  const gridTemplateStyle = useGridTemplateColumnStyle(columns)

  return (
    <Box
      className={classes.header}
      style={{ ...style, ...gridTemplateStyle }}
      {...props}
    >
      {columns.map((column, idx) => (
        <HeaderItem
          key={column.dataProperty}
          textAlign={idx === 0 ? "left" : "right"}
        >
          {column.title}
        </HeaderItem>
      ))}
    </Box>
  )
}

Header.displayName = "Header"

const ItemBarContext = createContext()

const useItem = () => {
  const { item } = useContext(ItemBarContext)
  return item
}
const useEditable = () => {
  const { editable } = useContext(ItemBarContext)
  return editable
}
const useDisabled = () => {
  const { isDisabled } = useContext(ItemBarContext)
  return isDisabled
}

export const ItemBar = ({
  style = {},
  item,
  columns,
  editable,
  isDisabled,
  onSave,
  children,
  deal_memo_order,
  ...props
}) => {
  const classes = useStyles()
  const gridTemplateStyle = useGridTemplateColumnStyle(columns)
  const billingContext = useContext(BillingContext)
  const roleData = useRoleGroup()

  const composedStyle = {
    ...style,
    ...gridTemplateStyle,
    gridTemplateRows: "1fr min-content  min-content min-content min-content",
  }
  const handleSave = (...args) => {
    if (onSave) {
      onSave(...args, item, deal_memo_order, roleData && roleData.role.user_id)
    }
  }
  const enhancedChildren = Children.map(children, child => {
    if (child.type) {
      if (child.type.displayName === "ButtonGroup") {
        return cloneElement(child, {
          editable,
        })
      }
    }
    return child
  })

  return (
    <ItemBarContext.Provider value={{ item, editable, isDisabled }}>
      <Formik
        initialValues={{
          type_name: item.type_name,
          item_type: item.item_type,
          quantity: item.quantity,
          discount: item.discount,
          actual_rate: item.actual_rate,
          description: item.description || "",
          client_rate:
            item.client_rate === null
              ? item.default_client_rate
              : item.client_rate,
          is_percentage:
            item.is_percentage === null
              ? item.default_is_percentage
              : item.is_percentage,
          class: item.class || "vendor",
          deal_memo_order: deal_memo_order || null,
        }}
        onSubmit={handleSave}
      >
        {billingContext &&
        billingContext.unsavedState.length > 0 &&
        billingContext.unsavedErrorBorder ? (
          billingContext.unsavedState.includes(`e${item.expense_id}`) ||
          billingContext.unsavedState.includes(
            `a${item.additional_expense_id}`
          ) ? (
            <Box
              {...props}
              style={composedStyle}
              className={classnames(classes.bar, classes.itemBarNotSaved)}
            >
              {enhancedChildren}
            </Box>
          ) : (
            <Box
              {...props}
              style={composedStyle}
              className={classnames(classes.bar, classes.itemBar)}
            >
              {enhancedChildren}
            </Box>
          )
        ) : (
          <Box
            {...props}
            style={composedStyle}
            className={classnames(classes.bar, classes.itemBar)}
          >
            {enhancedChildren}
          </Box>
        )}
      </Formik>
    </ItemBarContext.Provider>
  )
}

ItemBar.displayName = "ItemBar"

export const ItemsGroup = ({
  children,
  items,
  columns,
  setItems = () => {},
  editable = false,
  draggable = false,
  ...props
}) => {
  const billingContext = useContext(BillingContext)
  //dont let drag and drop if the item is not created
  const itemsWithOutBeCreated =
    (billingContext &&
      billingContext.unsavedState &&
      billingContext.unsavedState.filter(x => x.includes("-")) &&
      billingContext.unsavedState.filter(x => x.includes("-")).length > 0) ||
    false

  const classes = useStyles()
  //this is where we handle key logic for the items
  const roleData = useRoleGroup()
  const reorder = (startIndex, endIndex) => {
    const result = Array.from(items)
    const [removed] = result.splice(startIndex, 1)
    result.splice(endIndex, 0, removed)
    setItems(result.map((x, i) => ({ ...x, deal_memo_order: i + 1 })), roleData)
  }
  const onDragEnd = result => {
    if (!result.destination) {
      return
    }
    reorder(result.source.index, result.destination.index)
  }
  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="list" direction="vertical">
        {droppableProvided => (
          <div
            {...props}
            className={classes.itemsGroup}
            ref={droppableProvided.innerRef}
            {...droppableProvided.droppableProps}
          >
            {items
              .sort(function(a, b) {
                return a.deal_memo_order - b.deal_memo_order
              })
              .map((item, index) => {
                return (
                  <Draggable
                    isDragDisabled={itemsWithOutBeCreated || !draggable}
                    key={
                      item.expense_id
                        ? item.expense_id
                        : item.additional_expense_id
                    }
                    draggableId={
                      item.expense_id
                        ? `e${item.expense_id}`
                        : `a${item.additional_expense_id}`
                    }
                    index={index}
                  >
                    {(draggableProvided, draggableSnapshot) => {
                      return (
                        <div
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}
                          {...draggableProvided.dragHandleProps}
                        >
                          {cloneElement(children, {
                            item,
                            columns,
                            editable,
                            deal_memo_order: index + 1,
                            key: item.expense_id
                              ? item.expense_id
                              : item.additional_expense_id,
                          })}
                        </div>
                      )
                    }}
                  </Draggable>
                )
              })}
            {droppableProvided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  )
}

ItemsGroup.displayName = "ItemsGroup"

export const Item = ({
  children,
  valueForProperty,
  InputProps = {},
  TextFieldProps = {},
  additionalDisabled = false,
  ...props
}) => {
  const classes = useStyles()

  const item = useItem()
  const editable = useEditable()
  const isDisabled = useDisabled()

  const { values, setFieldValue } = useFormikContext()

  let isNumberTextfield =
    valueForProperty === "quantity" ||
    valueForProperty === "actual_rate" ||
    valueForProperty === "client_rate" ||
    valueForProperty === "discount"

  const handleChange = e => {
    if (e.target.value === "") {
      setFieldValue(valueForProperty, "")
      return
    }
    //if is a number input only let 2 decimals
    const twoDecimalsRegex = /^\d+(\.\d{0,2})?$/
    if (isNumberTextfield && twoDecimalsRegex.test(e.target.value)) {
      setFieldValue(valueForProperty, e.target.value)
    } else if (!isNumberTextfield) {
      setFieldValue(valueForProperty, e.target.value)
    }
  }

  let itemChildren = children
  if (children) {
    itemChildren = children
  } else if (editable) {
    //DESCRIPTION OF EXPENSE ITEM

    itemChildren = (
      <TextField
        disabled={isDisabled || additionalDisabled}
        label=""
        value={values[valueForProperty]}
        placeholder={
          valueForProperty === "description" ? "Enter Description" : null
        }
        multiline={props.haveGTT}
        type={isNumberTextfield ? "number" : "text"}
        variant="outlined"
        InputProps={{
          classes: { input: classes.textField },
          inputProps: {
            min: "0",
            step: valueForProperty === "quantity" ? "1" : "0.01",
          },
          style: { padding: "0px" },
          ///this will spread the Text Feild Props passed into an ExpenseTable.NumberItem if they are not passed an empty object is spread
          ...(InputProps ? InputProps : {}),
        }}
        fullWidth
        onChange={handleChange}
        {...TextFieldProps}
      />
    )
  } else {
    if (valueForProperty === "client_rate") {
      itemChildren = item.is_percentage
        ? item[valueForProperty] + " %"
        : "$ " + formatNumberWithCommas(item[valueForProperty])
    } else if (valueForProperty === "discount") {
      itemChildren =
        item[valueForProperty] > 0 &&
        `- ${formatNumberWithCommas(item[valueForProperty])}`
    } else if (valueForProperty === "actual_rate") {
      itemChildren =
        item[valueForProperty] > 0 &&
        `${formatNumberWithCommas(item[valueForProperty])}`
    } else {
      itemChildren = item[valueForProperty]
    }
  }

  return (
    <Box
      {...props}
      // {...values}
      className={classes.item}
      children={itemChildren}
    >
      {}
    </Box>
  )
}

Item.displayName = "Item"

export const NameItem = ({ additionalDisabled, ...props }) => {
  const isDisabled = useDisabled()
  const classes = useStyles()
  const { values, setFieldValue } = useFormikContext()
  const editable = useEditable()

  const expensesTypesList = useSelector(
    state => state.billing.expensesTypesList
  )

  return (
    <Item {...props} valueForProperty="type_name">
      {editable && (
        <Fragment>
          {expensesTypesList.length > 0 && (
            <AutocompleteBilling
              disabled={isDisabled || additionalDisabled}
              placeholder="Search Expense"
              getOptionLabel={option =>
                option.name + " - " + capitalizeFirstLetter(option.type)
              }
              value={expensesTypesList.filter(x => x.id === values.item_type)}
              className={classes.autocomplete}
              labelKey="name"
              blurInputOnSelect
              backspaceRemovesValue
              showValue
              options={expensesTypesList.filter(x => x.id !== 1)}
              caps
              onChange={value => {
                setFieldValue("item_type", value.id)
                setFieldValue("description", value.default_description)
                setFieldValue("client_rate", value.default_client_value || 0)
                setFieldValue("is_percentage", value.default_is_percentage)
                setFieldValue("type_name", value.name)
                setFieldValue("actual_rate", value.default_actual_rate || 0)
              }}
            />
          )}
          {expensesTypesList.length === 0 && (
            <Typography>Loading ...</Typography>
          )}
        </Fragment>
      )}
    </Item>
  )
}

NameItem.displayName = "Item.NameItem"

export const NumberItem = ({ TextFieldProps = {}, style = {}, ...props }) => {
  return (
    <Item
      {...props}
      style={
        viewportWidth > 600
          ? { ...style, justifyContent: "flex-end" }
          : { ...style, justifyContent: "center" }
      }
      TextFieldProps={{ ...TextFieldProps, type: "number" }}
    />
  )
}

NumberItem.displayName = "Item.NumberItem"

export const CalculatedItem = ({ children, ...props }) => {
  const { values } = useFormikContext()

  return (
    <Item
      {...props}
      justifyContent={viewportWidth > 600 ? "flex-end" : "center"}
    >
      {children(values)}
    </Item>
  )
}

CalculatedItem.displayName = "Item.CalculatedItem"

export const TotalItem = ({ children, ...props }) => {
  const { values } = useFormikContext()

  return (
    <Item
      {...props}
      justifyContent={viewportWidth > 600 ? "flex-end" : "center"}
      style={{
        color: values.class === "non-billable" ? "red" : "",
      }}
    >
      {children(values)}
    </Item>
  )
}

TotalItem.displayName = "Item.TotalItem"

const RoleGroupContext = createContext()

const useRoleGroup = () => {
  return useContext(RoleGroupContext)
}

export const RoleGroup = ({
  onClick,
  children,
  role,
  items,
  columns,
  editable,
  onSave,
  isDisabled,
  ...props
}) => {
  const classes = useStyles()
  const enhancedChildren = Children.map(children, child => {
    if (child.type) {
      if (child.type.displayName === "RoleBar") {
        return cloneElement(child, { role })
      }
      if (child.type.displayName === "ItemsGroup") {
        return cloneElement(child, {
          items,
          columns,
          editable,
        })
      }
      if (child.type.displayName === "AddButton") {
        return cloneElement(child, { role })
      }
      if (child.type.displayName === "SaveOrder") {
        return cloneElement(child, { role })
      }
      if (child.type.displayName === "ClassesAutocomplete") {
        return cloneElement(child, { role })
      }
      return child
    }
  })
  const handleSave = (...args) => {
    if (onSave) {
      onSave(...args, items, role && role.user_id)
    }
  }
  return (
    <RoleGroupContext.Provider value={{ role, items, isDisabled }}>
      <Formik
        initialValues={{
          commission_net: role.commission_net,
        }}
        onSubmit={handleSave}
      >
        <Box {...props} className={classes.roleGroup}>
          {enhancedChildren}
        </Box>
      </Formik>
    </RoleGroupContext.Provider>
  )
}

RoleGroup.displayName = "RoleGroup"

export const RoleBar = ({
  role,
  hideCrewNames,
  children,
  showStatus = false,
  ...props
}) => {
  const theme = useTheme()
  const classes = useStyles()

  const isGttMember = useIsGTT(role)
  const currentUserIsAdesk = useActiveCompanyAdesk()
  //we create this fake crew to add any expense that is not attached to a crew
  const isMiscExpenses =
    role.first_name === "Misc" && role.last_name === "Expenses"
  return (
    <Fragment>
      {hideCrewNames ? (
        <Box
          className={classnames(classes.bar, classes.roleBar)}
          style={{ alignItems: "flex-start" }}
        >
          {role.name}
          {/* <Box>
              <StatusChecker role={role} />
            </Box> */}
          <Box />
        </Box>
      ) : (
        <Fragment>
          <Box className={classnames(classes.bar, classes.roleBar)}>
            <Box className={classes.roleBarChildren}>
              <Box>
                {!isMiscExpenses && role.name}
                {role.first_name && (
                  <strong>
                    {!isMiscExpenses && " - "}
                    {role.first_name} {role.last_name}
                  </strong>
                )}
                {isGttMember &&
                  currentUserIsAdesk && (
                    <span
                      style={{
                        color: theme.palette.secondary.main,
                        fontWeight: 700,
                        textShadow: "1px 1px silver",
                        marginLeft: 10,
                      }}
                    >
                      GTT
                    </span>
                  )}
              </Box>
              <Box style={{ marginLeft: "12px" }}>
                {!isMiscExpenses &&
                  showStatus && <StatusChecker role={role} checkerSize={18} />}
              </Box>
            </Box>
            <Box className={classes.roleBarChildren}>
              {Boolean(children) && <Box>{children}</Box>}
            </Box>
          </Box>
        </Fragment>
      )}
    </Fragment>
  )
}

RoleBar.displayName = "RoleBar"

const ExpenseTable = ({ children = () => {}, columns, data }) => {
  //debugger
  const classes = useStyles()
  const dataIsArray = Array.isArray(data)
  const enhancedChildren = columns.map(columnsConfig => {
    return Children.map(children, child => {
      if (child.type) {
        if (child.type.displayName === "Header") {
          return cloneElement(child, {
            columns: columnsConfig.columns,
          })
        }
        if (child.type.displayName === "ItemsGroup") {
          // Filter out production services
          let expensesData = data[columnsConfig.dataProperty].filter(exp => {
            if (exp.item_type !== 174 && exp.item_type !== 177) {
              return exp
            }
          })
          return cloneElement(child, {
            //items: data[columnsConfig.dataProperty],
            items: expensesData,
            columns: columnsConfig.columns,
            editable: columnsConfig.editable,
          })
        }
        if (child.type.displayName === "RoleGroup") {
          if (dataIsArray) {
            return data.map(dataGroup => {
              return cloneElement(child, {
                role: {
                  role_id: dataGroup.role_id,
                  id: dataGroup.shoot_role || dataGroup.id,
                  name: dataGroup.name,
                  first_name: dataGroup.first_name,
                  last_name: dataGroup.last_name,
                  profile_image: dataGroup.profile_image,
                  user_id: dataGroup.user_id,
                  user_status: dataGroup.user_status,
                  commission_net: dataGroup.commission_net,
                  user_company: dataGroup.user_company,
                },
                items: dataGroup[columnsConfig.dataProperty],
                columns: columnsConfig.columns,
                editable: columnsConfig.editable,
              })
            })
          } else {
            return cloneElement(child, {
              role: {
                id: data.shoot_role,
                name: data.name,
                first_name: data.first_name,
                last_name: data.last_name,
                profile_image: data.profile_image,
              },
              items: data[columnsConfig.dataProperty],
              columns: columnsConfig.columns,
            })
          }
        }
      }
      return child
    })
  })

  return (
    <ExpenseTableContext.Provider
      value={{
        columns,
        data,
      }}
    >
      <Box className={classes.root}>{enhancedChildren}</Box>
    </ExpenseTableContext.Provider>
  )
}

export const SaveButton = props => {
  const classes = useStyles()
  const { submitForm, dirty, isSubmitting } = useFormikContext()
  const billingContext = useContext(BillingContext)
  const item = useItem()

  useEffect(
    () => {
      if (
        dirty === true &&
        billingContext &&
        !isUuid(item.expense_id || item.additional_expense_id)
      ) {
        billingContext.setUnsavedState(
          oldState =>
            item.expense_id
              ? [...oldState, `e${item.expense_id}`].filter(x => x)
              : [...oldState, `a${item.additional_expense_id}`].filter(x => x)
        )
      } else if (
        dirty === false &&
        billingContext &&
        !isUuid(item.expense_id || item.additional_expense_id)
      ) {
        billingContext.setUnsavedState(
          oldState =>
            item.expense_id
              ? [
                  ...oldState
                    .map(x => {
                      if (x !== `e${item.expense_id}`) {
                        return x
                      }
                    })
                    .filter(x => x),
                ]
              : [
                  ...oldState
                    .map(x => {
                      if (x !== `a${item.additional_expense_id}`) {
                        return x
                      }
                    })
                    .filter(x => x),
                ]
        )
      }
    },
    [dirty]
  )

  let saved = !isUuid(item.expense_id || item.additional_expense_id)
  const isDisabled = useDisabled() || (!dirty && saved) || isSubmitting

  return (
    <Box {...props}>
      <Button
        className={classes.saveButton}
        variant="outlined"
        disabled={isDisabled}
        onClick={submitForm}
      >
        {isSubmitting ? <CircularProgress size={10} /> : saved ? "Save" : "Add"}
      </Button>
    </Box>
  )
}

export const FlagButton = ({
  onClick,
  hideIfNotFlagged = false,
  crewView = false,
  ...props
}) => {
  // PROPS EXPLAINED:
  // hideIfNotFlagged => will not show flag button unless an item is flagged in a certain view (e.g., admin)
  // crewView => if true, show/handle crew flags (deal memo, bill approval pages for admin & crew) - otherwise only show/handle client flags (estimate, invoice for admin & client)
  const classes = useStyles()

  const authenticatedUser = useSelector(
    state => state.authentication.authenticatedUser
  )
  const item = useItem()
  const isDisabled = useDisabled()

  const type = crewView ? "crew" : "client"
  // type str used in conditional keys, values in the data object in the flag click fn
  // AND in the logic to hide flags / color flags

  const handleFlag = useCallback(
    () => {
      let nextStatusId = item[`${type}_flag_status`] === "flagged" ? 3 : 4
      let data = {
        [type + "_flag_status_id"]: nextStatusId,
        comment: item[`${type}_flag_comment`] || "",
      }
      onClick(data, item, authenticatedUser)
    },
    [
      item.expense_id,
      item.crew_flag_status,
      item.client_flag_status,
      authenticatedUser,
    ]
  )

  if (hideIfNotFlagged && item[`${type}_flag_status`] !== "flagged") {
    return null
  }
  return (
    <Box
      style={{
        position: "absolute",
        top: "50%",
        right: -48,
        transform: "translateY(-50%)",
      }}
      {...props}
    >
      <IconButton onClick={handleFlag} disabled={isDisabled}>
        <EmojiFlagsIcon
          className={classnames(classes.flag, {
            [classes.redFlag]: item[`${type}_flag_status`] === "flagged",
          })}
        />
      </IconButton>
    </Box>
  )
}

export const DeleteButton = ({ items, onClick, ...props }) => {
  const classes = useStyles()
  const item = useItem()
  const isDisabled = useDisabled()
  const billingContextDelete = useContext(BillingContext)

  const handleDelete = () => {
    if (onClick) {
      onClick(item, billingContextDelete && billingContextDelete.unsavedState)
    }
  }

  return (
    <Box>
      <Button
        {...props}
        className={classes.deleteButton}
        onClick={handleDelete}
        disabled={isDisabled}
      >
        Delete
      </Button>
    </Box>
  )
}

export const AddCrewMemberButton = ({ onClick, ...props }) => {
  const { role, isDisabled } = useRoleGroup()

  const handleAssign = useCallback(
    () => {
      if (onClick) {
        onClick(role)
      }
    },
    [role.id]
  )

  return (
    !role.user_id && (
      <IconButton
        size={viewportWidth < 600 ? "small" : "medium"}
        variant="outlined"
        color="primary"
        disabled={isDisabled}
        style={{
          padding: "6px",
        }}
        onClick={handleAssign}
      >
        <PersonAddIcon
          fontSize={viewportWidth < 600 ? "small" : "default"}
          fstyle={{ fontWeight: 600 }}
        />
      </IconButton>
    )
  )
}

AddCrewMemberButton.displayName = "AddCrewMemberButton"

export const DeleteRoleButton = ({ onClick, ...props }) => {
  const classes = useStyles()

  const { role, isDisabled } = useRoleGroup()

  const handleRoleDelete = useCallback(
    () => {
      if (onClick) {
        onClick(role)
      }
    },
    [role.id]
  )

  return (
    <IconButton
      {...props}
      onClick={handleRoleDelete}
      disabled={isDisabled}
      style={{ padding: "6px" }}
      size={viewportWidth < 600 ? "small" : "medium"}
    >
      <CancelIcon
        fontSize={viewportWidth < 600 ? "small" : "default"}
        className={classes.rejectedText}
      />
    </IconButton>
  )
}

DeleteRoleButton.displayName = "DeleteRoleButton"

export const AddButton = ({ role, onClick, ...props }) => {
  const { isDisabled } = useRoleGroup()

  const handleAdd = useCallback(
    () => {
      if (onClick) {
        onClick(role)
      }
    },
    [role.id]
  )

  return (
    <Box display="flex" justifyContent="flex-start">
      <Button
        color="secondary"
        onClick={handleAdd}
        disabled={isDisabled}
        size={viewportWidth < 600 ? "small" : "medium"}
        style={
          viewportWidth > 600 ? { fontSize: "18px" } : { fontSize: "13px" }
        }
      >
        <AddIcon
          fontSize={viewportWidth < 600 ? "small" : "default"}
          style={
            viewportWidth > 600 ? { fontSize: "18px" } : { fontSize: "13px" }
          }
        />
        Add Item
      </Button>
    </Box>
  )
}
AddButton.displayName = "AddButton"

export const CopyButton = ({ onClick, ...props }) => {
  const roleContext = useRoleGroup()

  function handleButtonClick() {
    if (onClick) {
      const copyValue = onClick(roleContext.role, roleContext.items)
      if (copyValue) {
        copyToClipboard(copyValue)
      }
    }
  }

  return (
    <IconButton
      {...props}
      onClick={handleButtonClick}
      size={viewportWidth < 600 ? "small" : "medium"}
      style={{
        padding: "6px",
      }}
    >
      <FileCopyIcon
        fontSize={viewportWidth < 600 ? "small" : "default"}
        style={{ fill: "black" }}
      />
    </IconButton>
  )
}

CopyButton.displayName = "CopyButton"

export const ClassesAutocomplete = (
  { position, onClick, ...props },
  styles = {}
) => {
  const isDisabled = useDisabled()
  const classes = useStyles()
  const [classArray, setClassArray] = useState([])
  const { values, setFieldValue } = useFormikContext()
  const editable = useEditable()

  const expensesTypesList = useSelector(
    state => state.billing.expensesTypesList
  )
  const usersRolesArray = useSelector(state => state.billing.usersRolesArray)
  const haveGTT = useHaveGTT(usersRolesArray)
  const currentUserIsAdesk = useActiveCompanyAdesk()

  const classesList = useSelector(state => state.billing.classesList)
  useEffect(
    () => {
      setClassArray(
        position.user_company === "Go To Team"
          ? [
              { name: "Expense", id: "expense" },
              {
                name: "Non-billable",
                id: "non-billable",
              },
              { name: "Vendor", id: "vendor" },
              ...classesList,
            ]
          : [
              { name: "Expense", id: "expense" },
              { name: "Vendor", id: "vendor" },
              ...classesList,
            ]
      )
    },
    [classesList]
  )

  return (
    //only if there is a go te team member in the shoot
    haveGTT &&
    currentUserIsAdesk && (
      <Fragment>
        <Box {...props} className={classes.classesAutocomplete}>
          {editable && (
            <Fragment>
              {classArray.length > 0 && (
                <AutocompleteBilling
                  disabled={isDisabled}
                  placeholder="Search Class"
                  getOptionLabel={option => option.name}
                  value={classArray.filter(
                    x =>
                      Boolean(parseFloat(values.class))
                        ? Number(x.id) === Number(values.class)
                        : x.id === values.class
                  )}
                  className={classes.autocomplete}
                  labelKey="name"
                  blurInputOnSelect
                  backspaceRemovesValue
                  showValue
                  options={classArray}
                  caps
                  onChange={value => {
                    setFieldValue("class", value.id)
                  }}
                />
              )}
              {expensesTypesList.length === 0 && (
                <Typography>Loading ...</Typography>
              )}
            </Fragment>
          )}
        </Box>
      </Fragment>
    )
  )
}

ClassesAutocomplete.displayName = "Item.ClassesAutocomplete"

// a drop down menu that will toggle the item is_percentage
export const PercentageButton = ({ onClick }, ...props) => {
  const classes = useStyles()
  const [openEl, setOpenEl] = useState(null)
  const menuEl = useRef(null)
  const item = useItem()
  const { values, setFieldValue } = useFormikContext()
  const editable = useEditable()
  const isDisabled = useDisabled()

  const handleClose = () => {
    setOpenEl(null)
  }
  const handleTogglePercentage = isPercent => {
    if (onClick) {
      onClick(isPercent, item)
    }
    setFieldValue("is_percentage", isPercent)
    handleClose()
  }
  const handleOpenClick = event => {
    setOpenEl(event.currentTarget)
  }
  return (
    <Fragment>
      {editable && (
        <Box>
          <Fragment>
            <InputAdornment
              position="start"
              ref={menuEl}
              onClick={isDisabled ? () => {} : handleOpenClick}
              size="small"
              aria-label="more"
            >
              <IconButton
                disabled={isDisabled}
                classes={{
                  root: classes.iconButton,
                }}
                aria-label="toggle percentage"
                size="small"
                height="12px"
              >
                <ExpandMoreIconOutlined
                  style={{ fontSize: "12px" }}
                  aria-label="toggle percentage"
                />
                <Typography>{values.is_percentage ? "%" : "$"}</Typography>
              </IconButton>
            </InputAdornment>

            <Menu
              className={classes.percentageMenu}
              id={item.expense_id || item.additional_expense_id}
              anchorEl={menuEl.current}
              open={Boolean(openEl)}
              onClose={handleClose}
            >
              <MenuItem onClick={() => handleTogglePercentage(0)}>$</MenuItem>
              <MenuItem onClick={() => handleTogglePercentage(1)}>%</MenuItem>
            </Menu>
          </Fragment>
        </Box>
      )}
    </Fragment>
  )
}
PercentageButton.displayName = "PercentageButton"

export const ButtonGroup = ({ children, editable, styles = {}, ...props }) => {
  if (editable) {
    return (
      <Box
        style={
          viewportWidth > 600
            ? {
                gridColumn: "actual_rate-start / total-end",
                gridRow: "2/2",
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                padding: "0 5px",
                ...styles,
              }
            : {
                gridColumn: "quantity-start / switch-end",
                gridRow: "4/4",
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "10px",
                marginBottom: "10px",
                marginRight: "10px",
                ...styles,
              }
        }
        {...props}
      >
        {children}
      </Box>
    )
  } else {
    return null
  }
}

ButtonGroup.displayName = "ButtonGroup"

export const CommissionNetInput = ({
  onClick,
  InputProps = {},
  TextFieldProps = {},
  ...props
}) => {
  const { role } = useRoleGroup()
  const classes = useStyles()
  const [loading, setLoading] = useState(false)
  const { values, setFieldValue, dirty, resetForm } = useFormikContext()
  const [open, setOpen] = useState(false)
  const billingContext = useContext(BillingContext)
  const usersRolesArray = useSelector(state => state.billing.usersRolesArray)
  const [localValue, setLocalValue] = useState()
  const showCommission = () => {
    setOpen(oldState => !oldState)
  }
  const isGttMember = useIsGTT(role)
  const currentUserIsAdesk = useActiveCompanyAdesk()
  const handleSaveCommissionNet = () => {
    if (onClick) {
      onClick(localValue, role.id, setLoading, values, resetForm, setOpen)
      setLoading(true)
    }
  }

  useEffect(
    () => {
      if (dirty === true && billingContext.unsavedState) {
        billingContext.setUnsavedState(
          oldState => role.id && [...oldState, `r${role.id}`].filter(x => x)
        )
      } else if (dirty === false && billingContext.unsavedState) {
        billingContext.setUnsavedState(
          oldState =>
            role.id && [
              ...oldState
                .map(x => {
                  if (x !== `r${role.id}`) {
                    return x
                  }
                })
                .filter(x => x),
            ]
        )
      }
    },
    [dirty]
  )

  const commissionNetDefaultValue = useMemo(
    () => {
      let total = 0
      usersRolesArray.forEach(user => {
        let newExpenses = [...user.expenses, ...user.additional_expenses]

        newExpenses.forEach(expense => {
          let totalExpense = Number(
            expense.quantity * expense.actual_rate +
              (expense.is_percentage
                ? (expense.actual_rate * expense.client_rate) / 100
                : expense.client_rate) -
              expense.discount
          )

          let markup = Number(
            expense.is_percentage
              ? (expense.actual_rate * expense.client_rate) / 100
              : expense.client_rate
          )
          //user has class that is a gtt id (gtt member)
          if (Boolean(parseFloat(expense.class))) {
            if (
              parseFloat(expense.class) === role.user_id &&
              user.user_company === "Go To Team"
            ) {
              total = total + totalExpense
            } else if (
              parseFloat(expense.class) === role.user_id &&
              user.user_company !== "Go To Team"
            ) {
              total = total + markup
            }
            //user has class that is not a gtt id (not a gtt member)
          } else if (
            expense.class === "non-billable" &&
            user.user_id === role.user_id
          ) {
            total = total - totalExpense
          } else if (
            expense.class === "vendor" &&
            user.user_id === role.user_id
          ) {
            total =
              total <= totalExpense
                ? totalExpense - total
                : total - totalExpense
          }
          // //user is apprentice
          if (user.apprentice !== null && user.apprentice === role.user_id) {
            if (parseFloat(expense.class)) {
              total = total + totalExpense
            } else if (expense.class === "non-billable") {
              total = total - totalExpense
            } else if (expense.class === "vendor") {
              total = total - totalExpense
            }
          }
        })
      })
      return total || 0
    },
    [billingContext]
  )

  const commissionNetValue = useMemo(
    () => {
      let total = {}
      usersRolesArray.forEach(user => {
        let isApprentice = user.apprentice !== null ? true : false

        let userId = user.user_id

        let newExpenses = [...user.expenses, ...user.additional_expenses]

        newExpenses.forEach(expense => {
          let totalExpense = Number(
            expense.quantity * expense.actual_rate +
              (expense.is_percentage
                ? (expense.actual_rate * expense.client_rate) / 100
                : expense.client_rate) -
              expense.discount
          )

          let markup = Number(
            expense.is_percentage
              ? (expense.actual_rate * expense.client_rate) / 100
              : expense.client_rate
          )

          totalExpense = expense.client_rate > 0 ? markup : totalExpense

          //user has class that is a gtt id (gtt member)
          if (Boolean(parseFloat(expense.class))) {
            if (isApprentice) {
              if (parseFloat(expense.class) === userId) {
                total[user.apprentice] =
                  (total[user.apprentice]
                    ? Number(total[user.apprentice])
                    : 0) + Number(totalExpense)
                total[user.user_id] =
                  (total[user.user_id] ? Number(total[user.user_id]) : 0) +
                  Number(totalExpense)
              } else {
                total[user.apprentice] =
                  (total[user.apprentice]
                    ? Number(total[user.apprentice])
                    : 0) + Number(totalExpense)
              }
            } else {
              if (parseFloat(expense.class) === userId) {
                total[user.user_id] =
                  (total[user.user_id] ? Number(total[user.user_id]) : 0) +
                  Number(totalExpense)
              } else {
                let classId = Number(expense.class)
                total[classId] =
                  (total[expense.class] ? Number(total[expense.class]) : 0) +
                  Number(totalExpense)
              }
            }
          } else if (expense.class === "non-billable") {
            total[user.user_id] =
              (total[user.user_id] ? Number(total[user.user_id]) : 0) -
              Number(totalExpense)
          }
        })
      })
      return total
    },
    [billingContext]
  )

  const handleChange = e => {
    if (/^[0-9]{0,6}(\.[0-9]{1,2})?$/.test(e.target.value)) {
      setFieldValue("commission_net", e.target.value)
      setLocalValue(e.target.value)
    }
  }

  const closeCommissionInput = () => {
    setOpen(false)
  }

  useEffect(() => {
    setLocalValue(
      (values && values.commission_net) || commissionNetValue[role.user_id]
    )
  }, [])

  return (
    isGttMember &&
    currentUserIsAdesk && (
      <Fragment>
        {open ? (
          <Box display="flex" alignItems="center">
            <Typography style={{ marginRight: "8px" }}>Class Pay:</Typography>
            <TextField
              label=""
              error={
                billingContext.unsavedErrorBorder &&
                billingContext.unsavedState.includes(`r${role.id}`)
                  ? true
                  : false
              }
              type="number"
              style={{ justifyContent: "center" }}
              value={localValue}
              placeholder="Commission Net"
              variant="outlined"
              InputProps={{
                classes: {
                  input: classes.commissionTextField,
                },
                endAdornment: (
                  <Typography style={{ marginLeft: "5px" }}>
                    {commissionNetValue[role.user_id]}
                  </Typography>
                ),
                inputProps: { min: "0", step: "1" },
                ///this will spread the Text Feild Props passed into an ExpenseTable.NumberItem if they are not passed an empty object is spread
                ...(InputProps ? InputProps : {}),
              }}
              onChange={handleChange}
              {...TextFieldProps}
            />
            <Box>
              <Tooltip title="Save Class Pay" aria-label="save class pay">
                <IconButton
                  size={viewportWidth < 600 ? "small" : "medium"}
                  style={{
                    padding: "6px",
                  }}
                  onClick={handleSaveCommissionNet}
                >
                  {loading ? (
                    <CircularProgress size="24px" />
                  ) : (
                    <CreateIcon
                      fontSize={viewportWidth < 600 ? "small" : "default"}
                    />
                  )}
                </IconButton>
              </Tooltip>
              <IconButton onClick={closeCommissionInput}>
                <CloseIcon
                  fontSize={viewportWidth < 600 ? "small" : "default"}
                />
              </IconButton>
            </Box>
          </Box>
        ) : (
          <Tooltip title="Open Class Pay" aria-label="open class pay">
            <IconButton
              style={{
                padding: "6px",
              }}
              size={viewportWidth < 600 ? "small" : "medium"}
              onClick={showCommission}
            >
              {loading ? (
                <CircularProgress size="24px" />
              ) : (
                <CreateIcon
                  fontSize={viewportWidth < 600 ? "small" : "default"}
                />
              )}
            </IconButton>
          </Tooltip>
        )}
      </Fragment>
    )
  )
}

CommissionNetInput.displayName = "CommissionNetInput"

ExpenseTable.Header = Header
ExpenseTable.ItemsGroup = ItemsGroup
ExpenseTable.ItemBar = ItemBar
ExpenseTable.Item = Item
ExpenseTable.NameItem = NameItem
ExpenseTable.NumberItem = NumberItem
ExpenseTable.TotalItem = TotalItem
ExpenseTable.RoleGroup = RoleGroup
ExpenseTable.RoleBar = RoleBar
ExpenseTable.SaveButton = SaveButton
ExpenseTable.DeleteButton = DeleteButton
ExpenseTable.AddButton = AddButton
ExpenseTable.FlagButton = FlagButton
ExpenseTable.ButtonGroup = ButtonGroup
ExpenseTable.CopyButton = CopyButton
ExpenseTable.AddCrewMemberButton = AddCrewMemberButton
ExpenseTable.DeleteRoleButton = DeleteRoleButton
ExpenseTable.PercentageButton = PercentageButton
ExpenseTable.CommissionNetInput = CommissionNetInput
ExpenseTable.ClassesAutocomplete = ClassesAutocomplete
ExpenseTable.CalculatedItem = CalculatedItem
export default ExpenseTable
